import { Fragment, useMemo, useRef, useState } from 'react'
import { RESUMED_QUIETLY_MS, fingerprint, useToolProgress } from './progress'
import { useI18n } from '@/i18n/I18nProvider'
import LeadGate from './LeadGate'
import { band } from './stepBands'
import type { SubmitState, ToolResult } from './submit'

/**
 * The Guts Check, ported from Kagan's standalone HTML.
 *
 * Twelve questions across four dimensions, each answer worth 0–3, and a level
 * from the average. The scoring is the source's and deliberately unchanged —
 * including the thresholds, which are not round numbers because they were tuned
 * so one generous answer cannot lift an organisation a whole level.
 *
 * Three phases: questions → gate → report. The gate is between them and not in
 * front, for the reason in `LeadGate.tsx`.
 *
 * The copy is data (`content/tools/guts-check.ts`, inside the dictionary), so a
 * locale translates it and the compiler names what is missing. This file holds
 * the arithmetic and the shape.
 */

/** The source's thresholds. Not round on purpose — see the header. */
function levelFor(avg: number): number {
  if (avg < 0.75) return 0
  if (avg < 1.55) return 1
  if (avg < 2.3) return 2
  return 3
}

export default function GutsCheck() {
  const { t } = useI18n()
  const tool = t.tools.gutsCheck
  const ui = t.tools.ui

  /** When the visitor arrived. The Worker's elapsed-time floor measures from here. */
  const openedAt = useRef(Date.now()).current

  const [phase, setPhase] = useState<'intro' | 'quiz' | 'gate' | 'report'>('intro')
  const [idx, setIdx] = useState(0)
  const [answers, setAnswers] = useState<(number | null)[]>(() =>
    tool.questions.map(() => null),
  )
  const [saved, setSaved] = useState<SubmitState>({ status: 'idle' })
  /** Set by pressing Next with nothing chosen — see the note beside the button. */
  const [needAnswer, setNeedAnswer] = useState(false)

  /*
    HALF-FINISHED ANSWERS SURVIVE A REFRESH — in this browser only. See
    `tools/progress.ts` for why this is not a row in our database.

    THE FINGERPRINT IS STRUCTURAL, NOT TEXTUAL, and here that took some care.
    The Diagnostic can hash its `DIAGNOSTIC_SCORES`, which is locale-independent
    by construction; this tool's questions come out of the dictionary instead, so
    hashing them would mean progress is discarded when a translator fixes a typo
    AND that a reader switching language loses their answers — both wrong, since
    the answers are INDEXES and stay valid through any rewording.

    So it hashes each question's `dim` and its option count. Those are shape, not
    prose: they change when a question is added, removed or re-dimensioned, and
    not when one is reworded. The residual it cannot catch, stated rather than
    pretended away: swapping two questions that have the same dimension and the
    same number of options. That would mis-restore two answers, and is a trade
    accepted against discarding everyone's progress on every copy edit.
  */
  const progress = useToolProgress({
    tool: 'agentic-guts-check',
    shape: fingerprint(tool.questions.map((q) => [q.dim, q.options.length])),
    state: useMemo(() => ({ idx, answers }), [idx, answers]),
    active: phase === 'quiz' || phase === 'gate',
    restore: (saved, ageMs) => {
      // Untrusted — the reader's own storage, checked against the live question
      // set before any of it is applied.
      if (!saved || !Array.isArray(saved.answers)) return
      if (saved.answers.length !== tool.questions.length) return
      const clean = saved.answers.map((a, i) =>
        typeof a === 'number' &&
        Number.isInteger(a) &&
        a >= 0 &&
        a < tool.questions[i].options.length
          ? a
          : null,
      )
      if (!clean.some((a) => a !== null)) return
      setAnswers(clean)
      setIdx(
        typeof saved.idx === 'number'
          ? Math.min(Math.max(Math.trunc(saved.idx), 0), tool.questions.length - 1)
          : 0,
      )
      if (ageMs < RESUMED_QUIETLY_MS) setPhase('quiz')
    },
  })

  const total = useMemo(
    () => answers.reduce<number>((a, b) => a + (b ?? 0), 0),
    [answers],
  )

  /** Average per dimension, 0–3. */
  const dimAvg = useMemo(() => {
    return tool.dims.map((_, d) => {
      const mine = tool.questions
        .map((q, i) => (q.dim === d ? answers[i] : null))
        .filter((v): v is number => v !== null)
      return mine.length ? mine.reduce((a, b) => a + b, 0) / mine.length : 0
    })
  }, [answers, tool.dims, tool.questions])

  const avg = total / tool.questions.length
  const level = levelFor(avg)
  const L = tool.levels[level]

  /**
   * The payload. Assembled here rather than in the gate so the gate stays a
   * form and knows nothing about scoring.
   *
   * `score` is rescaled to 0–100 because that is what the column accepts and
   * what makes three different tools comparable in a CRM list. The report still
   * shows the honest "x of 36", because that is the number the questions
   * actually produce.
   */
  const result: ToolResult = useMemo(
    () => ({
      score: Math.round((total / (tool.questions.length * 3)) * 100),
      verdict: `${ui.level} ${level} — ${L.name}`,
      band: L.band,
      dimensions: Object.fromEntries(
        tool.dims.map((d, i) => [d.name, Math.round(dimAvg[i] * 100) / 100]),
      ),
      answers: Object.fromEntries(
        tool.questions.map((q, i) => [
          q.text,
          answers[i] === null ? null : q.options[answers[i] as number],
        ]),
      ),
    }),
    [total, level, L, dimAvg, answers, tool.dims, tool.questions, ui.level],
  )

  // ---- intro -------------------------------------------------------------
  if (phase === 'intro') {
    return (
      <div className="mx-auto max-w-2xl text-center">
        {/*
          The button leads and the note follows, at a size above the site's
          standard pill — asked for on 2026-08-26, and the same order the other
          two tools now use. The first thing under the headline should be the
          thing a visitor came to do, not a paragraph about it.
        */}
        <button className="btn-primary px-12 py-5 text-xl" onClick={() => setPhase('quiz')}>
          {ui.start}
        </button>
        <p className="mt-4 text-sm text-muted-foreground">
          {ui.questionCount.replace('{n}', String(tool.questions.length))}
        </p>
        {/* Only a return visit reaches this — a refresh was already restored. */}
        {progress.resumedAt !== null && (
          <div className="mt-6">
            <p className="text-base text-muted-foreground">{ui.resumeNote}</p>
            <button className="mt-2 underline" onClick={() => setPhase('quiz')}>
              {ui.resume}
            </button>
          </div>
        )}
        <p className="mt-8 text-base leading-relaxed text-muted-foreground">{ui.introNote}</p>
      </div>
    )
  }

  // ---- questions ---------------------------------------------------------
  if (phase === 'quiz') {
    const q = tool.questions[idx]
    const dim = tool.dims[q.dim]
    const answered = answers[idx] !== null
    const last = idx === tool.questions.length - 1

    return (
      <div className="mx-auto max-w-2xl sm:max-w-4xl">
        <div className="flex items-baseline justify-between text-sm text-muted-foreground">
          <span>
            {ui.question} {idx + 1} / {tool.questions.length}
          </span>
          <span className="font-semibold text-foreground">{dim.name}</span>
        </div>
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-border">
          <div
            className="h-full bg-foreground transition-[width] [transition-duration:var(--transition-base)]"
            style={{ width: `${(idx / tool.questions.length) * 100}%` }}
          />
        </div>

        {/*
          Every question is laid out in the SAME grid, and the grid has one ROW
          PER PART: question and hint, then the options. Every question puts its
          own copy of a part in that part's row, so the row is as tall as the
          longest instance across the set and each part starts at the same y on
          every question — see the same note in Diagnostic.tsx, which explains
          why the reserved `sm:min-h` this replaces could not hold, and why one
          shared cell per question held the block still but not its contents.
        */}
        {/*
          One grid holds the question column and both buttons: from `sm` up they
          are the rails to the left and right of the options, vertically centred,
          so they sit beside the question instead of below the tallest one in the
          set; on a phone there is no room for rails and they drop to a row
          underneath. Same layout in Diagnostic.tsx and Ari.tsx.
        */}
        <div className="mt-8 grid grid-cols-2 items-center gap-4 sm:grid-cols-[auto_1fr_auto] sm:gap-6">
          <div className="col-span-2 row-start-1 grid min-w-0 gap-y-6 sm:col-span-1 sm:col-start-2">
          {tool.questions.map((qq, qi) => {
            const on = qi === idx
            return (
            <Fragment key={qi}>
              <div className={band(on) + ' row-start-1'} inert={on ? undefined : true}>
                <h3 className="text-2xl font-bold leading-tight tracking-tight text-foreground">
                  {qq.text}
                </h3>
                <p className="mt-2 text-base italic leading-relaxed text-muted-foreground">
                  {qq.hint}
                </p>
              </div>

              <div
                className={band(on) + ' row-start-2 space-y-3'}
                inert={on ? undefined : true}
                // Masked in session replay — see the note in Diagnostic.tsx.
                data-private
              >
                {qq.options.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      const next = [...answers]
                      next[qi] = i
                      setAnswers(next)
                    }}
                    className={
                      'flex w-full items-start gap-4 rounded-xl border p-5 text-left text-base leading-relaxed transition-colors [transition-duration:var(--transition-base)] ' +
                      (answers[qi] === i
                        ? 'border-foreground bg-muted font-medium text-foreground'
                        : 'border-border bg-background text-muted-foreground hover:border-foreground/40')
                    }
                  >
                    <span
                      className={
                        'mt-1 size-4 flex-none rounded-full border-2 ' +
                        (answers[qi] === i ? 'border-foreground bg-foreground' : 'border-border')
                      }
                    />
                    <span>{opt}</span>
                  </button>
                ))}
              </div>
            </Fragment>
            )
          })}
          </div>

          <button
            className="btn-secondary row-start-2 justify-self-start whitespace-nowrap sm:col-start-1 sm:row-start-1"
            onClick={() => {
              setNeedAnswer(false)
              setIdx((n) => Math.max(0, n - 1))
            }}
            style={{ visibility: idx === 0 ? 'hidden' : 'visible' }}
          >
            {ui.back}
          </button>
          {/*
            Both labels are always in the button, one invisible on top of the
            other, so it is as wide as the longer of the two on every question:
            swapping "Next" for "See the verdict" on the last step otherwise
            widened the right-hand rail and shifted the question column sideways.
          */}
          <button
            className="btn-primary row-start-2 grid justify-items-center justify-self-end whitespace-nowrap sm:col-start-3 sm:row-start-1"
            onClick={() => {
              if (!answered) return setNeedAnswer(true)
              setNeedAnswer(false)
              return last ? setPhase('gate') : setIdx((n) => n + 1)
            }}
          >
            <span className={'col-start-1 row-start-1' + (last ? '' : ' invisible')}>
              {ui.seeVerdict}
            </span>
            <span className={'col-start-1 row-start-1' + (last ? ' invisible' : '')}>
              {ui.next}
            </span>
          </button>
        </div>

        {/*
          Pressing Next with nothing chosen used to do nothing at all: the button
          was `disabled`, which reads as broken rather than as "answer first". It
          says so now, and the note clears itself the moment an option is picked.
          Below the buttons rather than above them, so showing it cannot move the
          row it is complaining about.
        */}
        {needAnswer && !answered && (
          <p role="alert" className="mt-4 text-sm font-medium text-destructive">
            {last ? ui.needAnswerLast : ui.needAnswer}
          </p>
        )}
      </div>
    )
  }

  // ---- the gate ----------------------------------------------------------
  if (phase === 'gate') {
    return (
      <LeadGate
        tool="agentic-guts-check"
        result={result}
        openedAt={openedAt}
        onDone={(state) => {
          setSaved(state)
          setPhase('report')
          progress.forget()
        }}
      />
    )
  }

  // ---- the report --------------------------------------------------------
  const ambition = dimAvg[0]
  const laggards = [dimAvg[1], dimAvg[2], dimAvg[3]]
  const weakest = Math.min(...laggards)
  const laggardName = tool.gap.laggards[laggards.indexOf(weakest)]

  return (
    <div className="mx-auto max-w-3xl">
      {/*
        Shown whether or not the save worked. A network error must not cost
        somebody the twelve answers they just gave — see LeadGate's header.
      */}
      {saved.status === 'failed' && (
        <p className="mb-6 rounded-lg border border-border bg-muted p-4 text-sm text-muted-foreground">
          {ui.notSaved}
        </p>
      )}

      <p className="text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {ui.level} {level} — {L.name}
      </p>
      <h3 className="mt-3 text-3xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl">
        {L.headline}
      </h3>
      <p className="mt-4 text-sm text-muted-foreground">
        {ui.scoreLine
          .replace('{total}', String(total))
          .replace('{max}', String(tool.questions.length * 3))
          .replace('{avg}', avg.toFixed(1))}
      </p>

      <div className="mt-8 space-y-4">
        {L.body.map((p, i) => (
          <p key={i} className="text-lg leading-relaxed text-muted-foreground">
            {p}
          </p>
        ))}
      </div>

      {/* The ladder, with the reader's rung marked. */}
      <div className="mt-12 grid gap-3 sm:grid-cols-4">
        {tool.ladder.map((r, i) => (
          <div
            key={i}
            className={
              'rounded-xl border p-4 ' +
              (i === level ? 'border-foreground bg-muted' : 'border-border bg-background')
            }
          >
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              {ui.level} {i}
              {i === level ? ` · ${ui.youAreHere}` : ''}
            </p>
            <p className="mt-1 text-base font-bold text-foreground">{r.name}</p>
            <p className="mt-1 text-sm leading-snug text-muted-foreground">{r.desc}</p>
          </div>
        ))}
      </div>

      {/* Dimension bars. */}
      <div className="mt-12 space-y-5">
        {tool.dims.map((d, i) => (
          <div key={i}>
            <div className="flex items-baseline justify-between">
              <p className="text-base font-semibold text-foreground">
                {d.name} <span className="font-normal text-muted-foreground">— {d.sub}</span>
              </p>
              <p className="text-sm tabular-nums text-muted-foreground">
                {dimAvg[i].toFixed(1)} / 3.0 · {ui.level} {levelFor(dimAvg[i])}
              </p>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-border">
              <div
                className="h-full rounded-full bg-foreground"
                style={{ width: `${(dimAvg[i] / 3) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* The gap callout — the sharpest thing on the page, so it is not buried. */}
      {ambition - weakest >= 0.9 && (
        <div className="mt-12 rounded-2xl border-l-4 border-l-foreground bg-muted p-6">
          <p className="text-base font-bold text-foreground">{tool.gap.aheadTitle}</p>
          {tool.gap.aheadBody.map((p, i) => (
            <p key={i} className="mt-2 text-base leading-relaxed text-muted-foreground">
              {p
                .replace('{ambition}', String(levelFor(ambition)))
                .replace('{weakest}', String(levelFor(weakest)))
                .replace('{laggard}', laggardName)}
            </p>
          ))}
        </div>
      )}
      {weakest - ambition >= 0.9 && (
        <div className="mt-12 rounded-2xl border-l-4 border-l-foreground bg-muted p-6">
          <p className="text-base font-bold text-foreground">{tool.gap.behindTitle}</p>
          {tool.gap.behindBody.map((p, i) => (
            <p key={i} className="mt-2 text-base leading-relaxed text-muted-foreground">
              {p}
            </p>
          ))}
        </div>
      )}

      <div className="mt-12 rounded-2xl border border-border p-6">
        <p className="text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {ui.provocation}
        </p>
        <p className="mt-3 text-xl font-semibold leading-snug text-foreground">{L.provocation}</p>
      </div>

      <div className="mt-12">
        <p className="text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {ui.moves}
        </p>
        <div className="mt-4 space-y-4">
          {L.moves.map((m, i) => (
            <div key={i} className="border-l-2 border-border pl-5">
              <p className="text-base font-bold text-foreground">{m.title}</p>
              <p className="mt-1 text-base leading-relaxed text-muted-foreground">{m.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/*
        The CTA the source HTML did not have at all — it ended in print, notes
        and restart, so a visitor finished and left. A tool that captures a lead
        and then offers the reader nothing is a form, not a conversation.
      */}
      <div className="mt-14 border-t border-border pt-10 text-center">
        <p className="text-xl font-semibold leading-snug text-foreground">{ui.ctaHeading}</p>
        {/* `#contact` rather than the Contact Us page: the form is at the foot
            of this very page, so the button scrolls to it. */}
        <a className="btn-primary mt-6 inline-flex" href="#contact">
          {ui.ctaLabel}
        </a>
        <p className="mt-6 text-sm text-muted-foreground">
          <button
            className="underline"
            onClick={() => {
              setAnswers(tool.questions.map(() => null))
              setIdx(0)
              progress.forget()
              setSaved({ status: 'idle' })
              setPhase('intro')
            }}
          >
            {ui.restart}
          </button>
        </p>
      </div>
    </div>
  )
}
